import Box from '@mui/material/Box';
import ListItem from '@mui/material/ListItem';
import Stack from '@mui/material/Stack';
import { useCallback } from 'react';
import type { LookalikeFormSegment } from '../../../../../../../../../modules/audiences/AudienceForm/types.ts';
import { SeedSegmentItem } from '../../../expandableSections/lookalike/SeedSegmentItem.tsx';
import { SimilaritySegmentItem } from '../../../expandableSections/lookalike/SimilaritySegmentItem.tsx';
import type { LookalikeDefinitionSegments } from '../../hooks/lookalike/lookalikeSegmentsReducer.ts';
import { itemStyles } from '../SegmentListItem/styles.ts';
import { ModeValues, useSegmentHeaderElements } from '../SegmentListItem/useSegmentHeaderElements.tsx';

export type SelectedLookalikeItemProps = {
  definition: LookalikeDefinitionSegments;
  onRemove: () => void;
  onEditLookalike?: (segment: LookalikeFormSegment) => void;
  dataTest?: string;
};

export const SelectedLookalikeItem = ({
  definition,
  onRemove,
  onEditLookalike,
  dataTest = 'selected-lookalike-item',
}: SelectedLookalikeItemProps) => {
  const { similaritySegment, seedSegment } = definition;

  const handleEdit = useCallback(() => {
    if (onEditLookalike && similaritySegment) {
      onEditLookalike(similaritySegment);
    }
  }, [onEditLookalike, similaritySegment]);

  const headerConfig = useSegmentHeaderElements({
    mode: ModeValues.lookalikeEditable,
    onEdit: handleEdit,
    onRemove,
  });

  if (!similaritySegment) {
    return null;
  }

  return (
    <ListItem data-test={dataTest} sx={itemStyles({ isSelected: false })}>
      <Stack
        className="segment-list-item"
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignContent: 'center',
          width: '100%',
        }}
      >
        <Box sx={{ minWidth: 0, overflow: 'hidden', flexGrow: 1 }}>
          <SimilaritySegmentItem
            segment={similaritySegment}
            isSelectedItem
            headerConfig={{ leftIcon: headerConfig.leftIcon, rightIcon: undefined }}
          />

          {seedSegment && <SeedSegmentItem segment={seedSegment} isSelectedItem={true} />}
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 'auto',
            flexShrink: 0,
          }}
        >
          {headerConfig.rightIcon}
        </Box>
      </Stack>
    </ListItem>
  );
};
